import React from 'react';
import { i18n } from 'config/locales';

import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { EmploymentActivityModel } from 'forms/sections/history/employment/types';
import { CommonOnUpdate, CommonOnError, CommonScrollIntoView } from 'types/props';
import { FieldModel } from 'forms/shared/types/field';
import { setAttribute } from 'components/Form/shared/setAttribute';
import Field from 'components/Form/Field';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';

interface Props extends ValidationElementProps, EmploymentActivityModel {
  className?: string
  onUpdate?: CommonOnUpdate
  disabled?: boolean
  required?: boolean
  onError?: CommonOnError
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
  requireEmploymentItemNonGovernment?: boolean
}

export default class EmploymentActivity extends ValidationElement<Props> {
  update = (queue: Partial<EmploymentActivityModel>) => {
    this.props.onUpdate({
      value: this.props.value,
      otherExplanation: this.props.otherExplanation,
      ...queue,
    });
  };

  updateActivity = (values: FieldModel) => {
    this.update({
      value: values.value,
      otherExplanation: '',
    });
  };

  // The type and logic doesn't exactly match up, but TextArea is configured to
  // return a FieldModel.
  updateExplanation = (values: FieldModel) => {
    this.update({
      otherExplanation: values.value,
    });
  };

  render() {
    return (
      <div className="employment-activity">
        <div className={this.props.className}>
          <Field
            attribute={setAttribute(this.props.attribute, 'value')}
            title={i18n.t(`history.employment.default.heading.activity`)}
            titleSize="h4"
            help="history.employment.default.activity.help"
            className={this.props.value === 'Other' ? 'no-margin-bottom' : ''}
            adjustFor="p"
            scrollIntoView={this.props.scrollIntoView}
          >
            <RadioGroup
              attribute={setAttribute(this.props.attribute, 'value')}
              name="employment_activity"
              className="option-list option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              selectedValue={this.props.value}
            >
              <label id="employment-activity">
                {i18n.t('history.employment.default.activity.title')}
              </label>

              <Radio
                label={i18n.t('history.employment.default.activity.type.activeMilitary')}
                value="ActiveMilitary"
                className="employment-activity-active"
                disabled={this.props.disabled}
                onUpdate={this.updateActivity}
                onError={this.props.onError}
                onBlur={this.props.onBlur}
                onFocus={this.props.onFocus}
              />
              <Radio
                label={i18n.t('history.employment.default.activity.type.nationalGuard')}
                value="NationalGuard"
                className="employment-activity-national"
                disabled={this.props.disabled}
                onUpdate={this.updateActivity}
                onError={this.props.onError}
                onBlur={this.props.onBlur}
                onFocus={this.props.onFocus}
              />
              <Radio
                label={i18n.t('history.employment.default.activity.type.usphs')}
                value="USPHS"
                className="employment-activity-usphs"
                disabled={this.props.disabled}
                onUpdate={this.updateActivity}
                onError={this.props.onError}
                onBlur={this.props.onBlur}
                onFocus={this.props.onFocus}
              />
              <Radio
                label={i18n.t('history.employment.default.activity.type.otherFederal')}
                value="OtherFederal"
                className="employment-activity-other-federal"
                disabled={this.props.disabled}
                onUpdate={this.updateActivity}
                onError={this.props.onError}
                onBlur={this.props.onBlur}
                onFocus={this.props.onFocus}
              />
              <Radio
                label={i18n.t('history.employment.default.activity.type.stateGovernment')}
                value="StateGovernment"
                className="employment-activity-state-government"
                disabled={this.props.disabled}
                onUpdate={this.updateActivity}
                onError={this.props.onError}
                onBlur={this.props.onBlur}
                onFocus={this.props.onFocus}
              />
              <Radio
                label={i18n.t('history.employment.default.activity.type.federalContractor')}
                value="FederalContractor"
                className="employment-activity-federal-contractor"
                disabled={this.props.disabled}
                onUpdate={this.updateActivity}
                onError={this.props.onError}
                onBlur={this.props.onBlur}
                onFocus={this.props.onFocus}
              />
              <label>{i18n.t('history.employment.default.activity.type.otherEmployment')}</label>
              {this.props.requireEmploymentItemNonGovernment && (
                <Radio
                  label={i18n.t('history.employment.default.activity.type.nonGovernment')}
                  value="NonGovernment"
                  className="employment-activity-nongovernment"
                  disabled={this.props.disabled}
                  onUpdate={this.updateActivity}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
              )}
              <Radio
                label={i18n.t('history.employment.default.activity.type.selfEmployment')}
                value="SelfEmployment"
                className="employment-activity-self"
                disabled={this.props.disabled}
                onUpdate={this.updateActivity}
                onError={this.props.onError}
                onBlur={this.props.onBlur}
                onFocus={this.props.onFocus}
              />
              <Radio
                label={i18n.t('history.employment.default.activity.type.unemployment')}
                value="Unemployment"
                className="employment-activity-unemployment"
                disabled={this.props.disabled}
                onUpdate={this.updateActivity}
                onError={this.props.onError}
                onBlur={this.props.onBlur}
                onFocus={this.props.onFocus}
              />
              <Radio
                label={i18n.t('history.employment.default.activity.type.other')}
                value="Other"
                className="employment-activity-other"
                disabled={this.props.disabled}
                onUpdate={this.updateActivity}
                onError={this.props.onError}
                onBlur={this.props.onBlur}
                onFocus={this.props.onFocus}
              />
            </RadioGroup>
          </Field>

          <Show when={this.props.value === 'Other'}>
            <Field
              attribute={setAttribute(this.props.attribute, 'otherExplanation')}
              title={i18n.t('history.employment.default.activity.other.label')}
              titleSize="label"
              adjustFor="labels"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                attribute={setAttribute(this.props.attribute, 'otherExplanation')}
                name="otherExplanation"
                className="other"
                value={this.props.otherExplanation}
                onUpdate={this.updateExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </Show>
        </div>
      </div>
    );
  }
}

EmploymentActivity.defaultProps = {
  onUpdate: (queue) => {},
  onError: (value, arr) => arr,
};
